import {
  Component,
  computed,
  effect,
  inject,
  signal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Button } from "src/app/shared/components/utils/button/button";
import { FilterSection } from "src/app/shared/components/filter-section/filter-section";
import { JourneySearchStore } from "../../store/journey-search-store";
import {
  FilterSet,
  FilterSetType,
} from "src/app/core/interfaces/filter-interface";

import { Router } from "@angular/router";
import { Loader } from "src/app/shared/components/loader/loader";
import { TranslocoModule } from "@jsverse/transloco";
import { PianoAnalyticsService } from "src/app/core/services/piano-analytics";
import {
  CreateFavoriteFilterSearchRequestInterface,
  SearchType,
} from "src/app/core/interfaces/favorite-interface";
import { FavoritesService } from "../../services/favorites/favorites-service";
import { InputModal } from "src/app/shared/components/utils/input-modal/input-modal";
import { LanguageService } from "src/app/core/services/language-service";
import { FavoriteStore } from "src/app/features/store/favorite-store";
import { FavoriteSearchErrorModal } from "src/app/shared/components/favorite-search-error-modal/favorite-search-error-modal";
import { FilterSubsection } from "src/app/shared/components/filter-subsection/filter-subsection";
import { ErrorModalService } from "src/app/core/services/error-modal-service";

@Component({
  selector: "app-journey-search-page",
  imports: [
    Button,
    FilterSection,
    Loader,
    TranslocoModule,
    InputModal,
    FavoriteSearchErrorModal,
    FilterSubsection,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: "./journey-search-page.html",
})
export class JourneySearchPage {
  filters = signal<FilterSet[]>([]);
  loading = signal<boolean>(false);
  filtersSelected = signal<string[]>([]);
  locationFiltersSelected = signal<FilterSet>(null);
  activitiesFiltersSelected = signal<FilterSet>(null);
  branchesFiltersSelected = signal<FilterSet>(null);
  branchesEmpty = signal<boolean>(true);
  activitiesEmpty = signal<boolean>(true);
  isActivityFilterUsed = signal<boolean>(false);
  localisationsEmpty = signal<boolean>(true);
  isJourneyImmature = signal<boolean>(false);
  isAddToFavoriteModalOpen = signal<boolean>(false);
  isFavoriteSearchSuccessSaved = signal<boolean>(false);

  favoritesService = inject(FavoritesService);
  favoriteStore = inject(FavoriteStore);
  store = inject(JourneySearchStore);
  router = inject(Router);
  piano = inject(PianoAnalyticsService);
  private readonly languageService = inject(LanguageService);
  private readonly errorModalService = inject(ErrorModalService);

  branchesFilters = computed(() =>
    this.filters().find((f) => f.filterSetType === FilterSetType.Sector)
  );
  activitiesFilters = computed(() =>
    this.filters().find((f) => f.filterSetType === FilterSetType.Activity)
  );
  localisationFilters = computed(() =>
    this.filters().find(
      (f) => f.filterSetType === FilterSetType.GeographicalLocation
    )
  );

  constructor() {
    effect(() => {
      this.store.setJourneyMaturity(this.isJourneyImmature());
    });
    effect(() => {
      this.languageService.currentLanguage();
      this.loadFilters();
    });
  }

  async loadFilters(): Promise<void> {
    this.loading.set(true);
    if (this.store.activitiesFiltersSelected()) {
      this.activitiesFiltersSelected.set(
        this.store.activitiesFiltersSelected()
      );
    }
    if (this.store.branchesFiltersSelected()) {
      this.branchesFiltersSelected.set(this.store.branchesFiltersSelected());
    }
    if (this.store.locationFiltersSelected()) {
      this.locationFiltersSelected.set(this.store.locationFiltersSelected());
    }
    try {
      await this.store.loadJourneyFilters();

      const filters: FilterSet[] = [
        this.store.activitiesFilters(),
        this.store.branchesFilters(),
        this.store.locationFilters(),
      ];

      this.store.updateSelectedFiltersTranslations();

      if (this.store.activitiesFiltersSelected()) {
        this.activitiesFiltersSelected.set(
          this.store.activitiesFiltersSelected()
        );
      }
      if (this.store.branchesFiltersSelected()) {
        this.branchesFiltersSelected.set(this.store.branchesFiltersSelected());
      }
      if (this.store.locationFiltersSelected()) {
        this.locationFiltersSelected.set(this.store.locationFiltersSelected());
      }

      this.filters.set(filters);
    } catch (error) {
      if (this.errorModalService.shouldOpenErrorModal(error)) {
        this.errorModalService.openErrorModal();
      }
      this.filters.set([]);
    } finally {
      this.loading.set(false);
    }
  }

  onBranchesEmpty(isEmpty: boolean) {
    this.branchesEmpty.set(isEmpty);
  }

  onActivitiesEmpty(isEmpty: boolean) {
    this.activitiesEmpty.set(isEmpty);
  }

  onLocalisationsEmpty(isEmpty: boolean) {
    this.localisationsEmpty.set(isEmpty);
  }

  isFiltersEmpty = computed(
    () =>
      this.branchesEmpty() ||
      (this.activitiesEmpty() && this.isActivityFilterUsed()) ||
      this.localisationsEmpty()
  );

  canSearchJourney(): boolean {
    if (this.isJourneyImmature()) {
      return true;
    } else if (!this.isFiltersEmpty()) {
      return true;
    } else {
      return false;
    }
  }

  checkAllBranchesFiltersAreDisableActivityFilter(): boolean {
    if (this.branchesFiltersSelected().filters.length === 0) {
      return true;
    }
    return this.branchesFiltersSelected().filters.every(
      (selectedFilter) =>
        selectedFilter.customProperties !== null &&
        "disableActivityFilter" in selectedFilter.customProperties &&
        selectedFilter.customProperties["disableActivityFilter"] === "true"
    );
  }

  updateFiltersSelected(filterUpdated: FilterSet) {
    this.isFavoriteSearchSuccessSaved.set(false);
    switch (filterUpdated.filterSetType) {
      case FilterSetType.Sector:
        this.branchesFiltersSelected.set(filterUpdated);

        if (this.checkAllBranchesFiltersAreDisableActivityFilter()) {
          this.isActivityFilterUsed.set(false);
        } else {
          this.isActivityFilterUsed.set(true);
        }

        break;
      case FilterSetType.Activity:
        this.activitiesFiltersSelected.set(filterUpdated);
        break;
      case FilterSetType.GeographicalLocation:
        this.locationFiltersSelected.set(filterUpdated);
        break;
    }
  }

  launchJourneySearch(): void {
    this.store.setJourneyMaturity(this.isJourneyImmature());
    this.store.setFiltersSelected(
      this.locationFiltersSelected()!,
      this.branchesFiltersSelected()!,
      this.isActivityFilterUsed() ? this.activitiesFiltersSelected() : null
    );
    this.piano.sendEvent("journey.start", {});
    this.router.navigate(["/journey/clarify"]);
  }

  async saveSearch(searchName: string): Promise<void> {
    const createFavoriteFilterSearchRequest: CreateFavoriteFilterSearchRequestInterface =
      {
        name: searchName,
        searchType: SearchType.JourneySearch,
        research: {
          selectedFilters: [
            this.locationFiltersSelected(),
            this.branchesFiltersSelected(),
            this.activitiesFiltersSelected(),
          ],
        },
      };

    try {
      await this.favoritesService.saveSearch(createFavoriteFilterSearchRequest);
    } catch {
      this.favoriteStore.setFavoriteSearchesErrorModalOpen(true);
    } finally {
      this.isFavoriteSearchSuccessSaved.set(true);
      this.isAddToFavoriteModalOpen.set(false);
    }
  }

  openAddFavoriteModal(): void {
    this.isAddToFavoriteModalOpen.set(true);
  }

  closeAddFavoriteModal(): void {
    this.isAddToFavoriteModalOpen.set(false);
  }
}
